import { useCallback, useEffect, useRef, useState } from "react"
import { get } from "helpers/api_helper"
import { LANGUAGES } from "./catalogView"

const LIMIT_KEYS = ["activeThemes", "activeReasonsPerTheme", "title", "reason", "reasonsLabel"]
// The catalog as GET /admin/tournament-feedback/catalog sends it (sportya-api docs/tournament-feedback.md), or null.
const validCatalog = data => typeof data?.feature?.enabled === "boolean" && Array.isArray(data.themes)
  && data.themes.every(theme => Array.isArray(theme?.reasons)) && LIMIT_KEYS.every(key => Number.isSafeInteger(data.limits?.[key]))
  && typeof data.other?.texts?.ro === "string" ? data : null

// While a translation runs, the catalog is read again every 5 s, for at most two minutes after the last change.
const TRANSLATION_POLL_MS = 5000
const TRANSLATION_POLLS = 24
const translating = data => data.themes.some(theme => [theme, ...theme.reasons]
  .some(item => LANGUAGES.some(([language]) => item.variants?.[language]?.status === "pending")))

// The whole catalog with the feature's state in this environment, read once for both tabs. `read` is `{ loading }`,
// `{ error }` or `{ data }`; `retry` reads it again from scratch. `reload` reads it again after a change while the current
// catalog stays on screen; if that read fails, `read` keeps the catalog and adds `error`. While a translation runs the
// catalog is reloaded the same way, so „Translating…” turns into the translation. Nothing is read until `enabled` (an
// active SuperAdmin).
export default function useFeedbackCatalog(enabled) {
  const [read, setRead] = useState({ loading: true })
  const [attempt, setAttempt] = useState({ count: 0, quiet: false })
  const polls = useRef(0)
  useEffect(() => {
    if (!enabled) return undefined
    let current = true
    if (!attempt.quiet) setRead({ loading: true })
    get("/admin/tournament-feedback/catalog").then(response => {
      const data = validCatalog(response?.data)
      if (!data) throw new Error("Invalid tournament feedback catalog")
      if (current) setRead({ data })
    }).catch(() => { if (current) setRead(previous => attempt.quiet && previous.data ? { data: previous.data, error: true } : { error: true }) })
    return () => { current = false }
  }, [enabled, attempt])
  useEffect(() => {
    if (!read.data || read.error || !translating(read.data) || polls.current >= TRANSLATION_POLLS) return undefined
    const timer = setTimeout(() => {
      polls.current += 1
      setAttempt(previous => ({ count: previous.count + 1, quiet: true }))
    }, TRANSLATION_POLL_MS)
    return () => clearTimeout(timer)
  }, [read])
  const retry = useCallback(() => { polls.current = 0; setAttempt(previous => ({ count: previous.count + 1, quiet: false })) }, [])
  const reload = useCallback(() => { polls.current = 0; setAttempt(previous => ({ count: previous.count + 1, quiet: true })) }, [])
  return { read, retry, reload }
}
